<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="800px">
    <v-card class="details-dialog-card">
      <v-toolbar class="elevation-2" color="primary">
        <v-toolbar-title class="ml-0">
          <span class="font-weight-bold white--text">
            {{ $t('coinHistory.detailsTitle') }}
          </span>
        </v-toolbar-title>
        <v-spacer />
        <v-btn @click="close" icon dark>
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text class="px-0 pb-0 details-dialog-content">
        <v-container>
          <v-card v-if="invoice" class="pb-2 mb-4">
            <v-card-title class="pb-2">
              {{ $t('coinHistory.invoice') }}
            </v-card-title>
            <v-simple-table class="do-not-apply-hover" dense>
              <template v-slot:default>
                <tbody>
                  <tr>
                    <td>{{ $t('entities.invoices.attributes.number') }}</td>
                    <td class="font-weight-bold">
                      {{ invoice.number || '—' }}
                    </td>
                    <td>{{ $t('miscellaneous.createdAt') }}</td>
                    <td class="font-weight-bold">
                      {{ formatDateValue(invoice.created_at) }}
                    </td>
                  </tr>
                  <tr>
                    <td>{{ $t('miscellaneous.gross') }}</td>
                    <td class="font-weight-bold">
                      {{ formatGrossAmount(invoice.total_gross_amount) }}
                    </td>
                    <td>{{ $t('entities.invoices.attributes.byUser') }}</td>
                    <td class="font-weight-bold">
                      {{ invoice.seller_user_name || '—' }}
                    </td>
                  </tr>
                  <tr>
                    <td>{{ $t('entities.invoices.attributes.toUser') }}</td>
                    <td class="font-weight-bold">
                      {{ getUserDisplayName(invoice.buyer_user_id) }}
                    </td>
                    <td>{{ $t('entities.paymentMethods.entity') }}</td>
                    <td class="font-weight-bold">
                      {{ getPaymentMethodName(invoice.payment_method_id) }}
                    </td>
                  </tr>
                  <tr>
                    <td>{{ $t('entities.invoices.attributes.fiscalized') }}</td>
                    <td>
                      <v-chip
                        :color="
                          invoice.fiscalization_required ? 'red' : 'purple'
                        "
                        small
                        dark
                        pill>
                        {{ invoice.fiscalization_required ? 'No' : 'Yes' }}
                      </v-chip>
                    </td>
                    <td>{{ $t('miscellaneous.arena') }}</td>
                    <td class="font-weight-bold">{{ arenaName || '—' }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>

          <v-card v-if="transaction" class="pb-2">
            <v-card-title class="pb-2">
              {{ $t('coinHistory.transaction') }}
            </v-card-title>
            <v-simple-table class="do-not-apply-hover" dense>
              <template v-slot:default>
                <tbody>
                  <tr>
                    <td>{{ $t('entities.invoices.attributes.id') }}</td>
                    <td class="font-weight-bold">
                      {{ transaction.id || '—' }}
                    </td>
                    <td>{{ $t('entities.invoices.attributes.coins') }}</td>
                    <td class="font-weight-bold">
                      {{ transaction.coins_given ?? '—' }}
                    </td>
                  </tr>
                  <tr>
                    <td>{{ $t('entities.invoices.attributes.byUser') }}</td>
                    <td class="font-weight-bold">
                      {{ getUserDisplayName(transaction.seller_user_id) }}
                    </td>
                    <td>{{ $t('entities.invoices.attributes.toUser') }}</td>
                    <td class="font-weight-bold">
                      {{ getUserDisplayName(transaction.buyer_user_id) }}
                    </td>
                  </tr>
                  <tr>
                    <td>{{ $t('miscellaneous.createdAt') }}</td>
                    <td class="font-weight-bold">
                      {{ formatDateValue(transaction.created_at) }}
                    </td>
                    <td>{{ $t('miscellaneous.arena') }}</td>
                    <td class="font-weight-bold">{{ arenaName || '—' }}</td>
                  </tr>
                  <tr v-if="transaction.restoring_transaction_id">
                    <td>{{ $t('coinHistory.restoringTransactionId') }}</td>
                    <td class="font-weight-bold" colspan="3">
                      {{ transaction.restoring_transaction_id }}
                    </td>
                  </tr>
                  <tr v-if="transaction.restoring_reason">
                    <td>{{ $t('coinHistory.restoringReason') }}</td>
                    <td class="font-weight-bold" colspan="3">
                      {{ transaction.restoring_reason }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>
        </v-container>
      </v-card-text>
      <v-card-actions class="details-dialog-actions">
        <v-spacer />
        <v-btn @click="close" color="dark darken-1" text>
          {{ $t('miscellaneous.ok') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import formatDate from '@/mixins/format-date'

export default {
  name: 'CoinDetailsDialog',
  mixins: [formatDate],
  props: {
    value: { type: Boolean, default: false },
    invoice: { type: Object, default: null },
    transaction: { type: Object, default: null },
    arenaName: { type: String, default: '' },
    currencySymbol: { type: String, default: '' },
    paymentMethodsById: { type: Object, default: () => ({}) },
    usersById: { type: Object, default: () => ({}) },
  },
  methods: {
    close() {
      this.$emit('input', false)
    },
    formatDateValue(value) {
      if (!value) return '—'
      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) return value
      return this.formatDate(parsed, true, true)
    },
    formatGrossAmount(amount) {
      return amount ? `${amount} ${this.currencySymbol}`.trim() : '—'
    },
    getPaymentMethodName(id) {
      if (!id) return '/'
      return this.paymentMethodsById[id]?.name || id
    },
    getUserDisplayName(userId) {
      if (!userId) return '/'
      const user = this.usersById[userId]
      if (!user) return userId
      if (user.identity?.name) return user.identity.name
      if (user.name) return user.name
      if (user.username) return user.username
      return userId
    },
  },
}
</script>

<style scoped>
.details-dialog-card {
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

.details-dialog-content {
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
}

.details-dialog-actions {
  flex: 0 0 auto;
}
</style>
